import { useMemo, useState } from 'react';
import { Plus, Trash2, Pencil, UserPlus, KeyRound } from 'lucide-react';
import { Link, useSearchParams } from 'react-router-dom';
import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import { cn } from '@/lib/cn';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Skeleton } from '@/components/ui/Skeleton';
import { EmptyState } from '@/components/ui/EmptyState';
import { Badge } from '@/components/ui/Badge';
import { toast } from '@/components/ui/Toast';
import { useMembers, useDeleteMember, useManageableCompanies, useRoles } from './api';
import type { User } from '@/lib/api/types';
import { MemberFormDialog } from './components/MemberFormDialog';
import { SetPasswordDialog } from './components/SetPasswordDialog';
import { useAuthStore } from '@/lib/auth/authStore';
import { useCanAccess } from '@/lib/permissions/useCanAccess';
import { useCurrentPlan } from '@/lib/permissions/usePlanQuota';
import { UpgradeDialog } from '@/features/billing/UpgradeDialog';
import { QuotaBanner } from '@/features/billing/QuotaBanner';
import { useT } from '@/lib/i18n/useT';

export function MembersTab() {
  const sessionApp = useAuthStore((s) => s.app) ?? undefined;
  const currentUserId = useAuthStore((s) => s.user?.id);
  const t = useT();
  const { data, isLoading } = useMembers({ appType: sessionApp });
  const companies = useManageableCompanies();
  const roles = useRoles({ appType: sessionApp });
  const deleteMember = useDeleteMember();
  const canCreate = useCanAccess('User', 'create');
  const canEdit = useCanAccess('User', 'edit');
  const canDelete = useCanAccess('User', 'delete');
  // The backend requires `User:full` (and same company) to mutate someone
  // else's password — gate the button on the same condition.
  const canSetPasswordForOthers = useCanAccess('User', 'full');
  const canMutate = canEdit || canDelete || canSetPasswordForOthers;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [upgradeOpen, setUpgradeOpen] = useState(false);
  const [editing, setEditing] = useState<User | null>(null);
  const [passwordFor, setPasswordFor] = useState<User | null>(null);
  const [searchParams] = useSearchParams();
  const { data: planUsage } = useCurrentPlan();
  // Use the live `usersUsed` that `/plans/current` now computes server-side
  // (COUNT(*) on the agency users) — the access-manager listing is scoped to
  // the caller's manageable set and would under-count the agency total.
  const usersLimit = planUsage?.maxUsers ?? 0;
  const usersUsed =
    planUsage?.usersUsed ?? data?.data?.total ?? data?.data?.items?.length ?? 0;
  const usersReached = usersLimit > 0 && usersUsed >= usersLimit;
  const openCreateMember = () => {
    if (usersReached) {
      setUpgradeOpen(true);
      return;
    }
    setEditing(null);
    setDialogOpen(true);
  };

  function roleLink(roleId: number): string {
    const params = new URLSearchParams(searchParams);
    params.set('tab', 'roles');
    params.set('editRole', String(roleId));
    return `?${params.toString()}`;
  }

  const items = data?.data?.items ?? [];
  const total = data?.data?.total ?? 0;
  const companyMap = new Map<number, string>(
    (companies.data?.data?.items ?? []).map((c) => [c.id, c.name]),
  );
  const roleDescriptionById = useMemo(
    () =>
      new Map<number, string>(
        (roles.data?.data?.items ?? [])
          .filter((r) => r.description)
          .map((r) => [r.id, r.description as string]),
      ),
    [roles.data],
  );

  function onDelete(m: User) {
    if (!confirm(t('members.confirmDelete', { email: m.email }))) return;
    deleteMember.mutate(m.id, {
      onSuccess: () => toast({ tone: 'success', title: t('members.deletedToast') }),
      onError: (e: Error) => toast({ tone: 'error', title: e.message }),
    });
  }

  return (
   <TooltipPrimitive.Provider delayDuration={150}>
    <Card>
      <CardHeader className="flex flex-row items-center justify-between gap-4">
        <CardTitle>
          {t('members.title')} {total > 0 && <span className="text-sm font-normal text-[var(--color-fg-muted)]">({total})</span>}
        </CardTitle>
        {canCreate && (
          <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreateMember}>
            {t('members.addMember')}
          </Button>
        )}
      </CardHeader>
      <QuotaBanner resourceLabel="users" limit={usersLimit} used={usersUsed} />
      <CardContent className="p-0">
        {isLoading ? (
          <div className="p-6"><Skeleton className="h-40" /></div>
        ) : items.length === 0 ? (
          <EmptyState
            icon={<UserPlus className="h-6 w-6" />}
            title={t('members.empty.title')}
            description={t('members.empty.body')}
            action={
              canCreate ? (
                <Button leftIcon={<Plus className="h-4 w-4" />} onClick={openCreateMember}>
                  {t('members.addMember')}
                </Button>
              ) : undefined
            }
          />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[var(--color-bg-elevated)] text-left text-xs uppercase tracking-wide text-[var(--color-fg-muted)]">
                <tr>
                  <th className="px-4 py-3">{t('members.col.name')}</th>
                  <th className="px-4 py-3">{t('members.col.email')}</th>
                  <th className="px-4 py-3">{t('members.col.company')}</th>
                  <th className="px-4 py-3">{t('members.col.role')}</th>
                  {canMutate && <th className="px-4 py-3 text-right">{t('members.col.actions')}</th>}
                </tr>
              </thead>
              <tbody className="divide-y divide-[var(--color-border-subtle)]">
                {items.map((m) => {
                  const roleLabel =
                    typeof m.role === 'string' ? m.role : m.role?.name ?? '—';
                  const roleId =
                    typeof m.role === 'object' && m.role ? m.role.id : null;
                  const isSelf = m.id === currentUserId;
                  return (
                    <tr key={m.id} className="hover:bg-[var(--color-bg-elevated)]">
                      <td className="px-4 py-3 font-semibold">
                        {[m.firstname, m.lastname].filter(Boolean).join(' ') || '—'}
                        {isSelf && <Badge tone="neutral" className="ml-2">{t('members.youBadge')}</Badge>}
                      </td>
                      <td className="px-4 py-3 text-[var(--color-fg-muted)]">{m.email}</td>
                      <td className="px-4 py-3">
                        {m.companyId ? (companyMap.get(m.companyId) ?? `#${m.companyId}`) : '—'}
                      </td>
                      <td className="px-4 py-3">
                        {roleId ? (
                          <RoleChip
                            label={roleLabel}
                            description={roleDescriptionById.get(roleId) ?? null}
                            to={roleLink(roleId)}
                            hoverTitle={t('members.openRoleTitle', { role: roleLabel })}
                          />
                        ) : (
                          <Badge tone="info">{roleLabel}</Badge>
                        )}
                      </td>
                      {canMutate && (
                        <td className="px-4 py-3 text-right">
                          {canEdit && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setEditing(m); setDialogOpen(true); }}
                              leftIcon={<Pencil className="h-3.5 w-3.5" />}
                            >
                              {t('common.edit')}
                            </Button>
                          )}
                          {canSetPasswordForOthers && !isSelf && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setPasswordFor(m)}
                              leftIcon={<KeyRound className="h-3.5 w-3.5" />}
                              title={t('members.setPassword.action')}
                            >
                              {t('members.setPassword.action')}
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => onDelete(m)}
                              leftIcon={<Trash2 className="h-3.5 w-3.5" />}
                              disabled={isSelf}
                              title={isSelf ? t('members.cantDeleteSelf') : undefined}
                            >
                              {t('common.delete')}
                            </Button>
                          )}
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </CardContent>

      <MemberFormDialog
        open={dialogOpen}
        onOpenChange={setDialogOpen}
        editing={editing}
        companies={companies.data?.data?.items ?? []}
        roles={roles.data?.data?.items ?? []}
        onQuotaExceeded={() => setUpgradeOpen(true)}
      />

      <SetPasswordDialog
        open={passwordFor !== null}
        onOpenChange={(o) => !o && setPasswordFor(null)}
        member={passwordFor}
      />
      <UpgradeDialog
        open={upgradeOpen}
        onOpenChange={setUpgradeOpen}
        resource="maxUsers"
        currentPlanName="Free"
        limit={usersLimit}
      />
    </Card>
   </TooltipPrimitive.Provider>
  );
}

function RoleChip({
  label,
  description,
  to,
  hoverTitle,
}: {
  label: string;
  description: string | null;
  to: string;
  hoverTitle: string;
}) {
  const chip = (
    <Link
      to={to}
      className={cn(
        'inline-flex items-center rounded-full bg-[color-mix(in_srgb,var(--color-info)_10%,transparent)] px-2.5 py-0.5 text-xs font-semibold uppercase tracking-wide text-[var(--color-info)]',
        'transition-colors hover:bg-[color-mix(in_srgb,var(--color-info)_18%,transparent)] hover:underline',
      )}
      title={description ? undefined : hoverTitle}
    >
      {label}
    </Link>
  );
  if (!description) return chip;
  return (
    <TooltipPrimitive.Root>
      <TooltipPrimitive.Trigger asChild>{chip}</TooltipPrimitive.Trigger>
      <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
          side="top"
          align="start"
          sideOffset={6}
          className="z-50 max-w-xs rounded-lg border border-[var(--color-border-default)] bg-[var(--color-bg-elevated)] px-3 py-2 text-xs text-[var(--color-fg-primary)] shadow-[var(--shadow-elevated)]"
        >
          <p className="mb-0.5 text-[10px] font-extrabold uppercase tracking-wider text-[var(--color-fg-subtle)]">
            {label}
          </p>
          <p className="whitespace-pre-line">{description}</p>
          <TooltipPrimitive.Arrow className="fill-[var(--color-bg-elevated)]" />
        </TooltipPrimitive.Content>
      </TooltipPrimitive.Portal>
    </TooltipPrimitive.Root>
  );
}
